import { Platform } from 'react-native';

declare const __DEV__: boolean | undefined;

const PREFIX = '[HomeDbg]';

/** Activar: localStorage `vuelto:debug:home=1` o EXPO_PUBLIC_DEBUG_HOME=1. Default: apagado (evita spam en consola). */
export function isHomeDebugEnabled(): boolean {
  if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
    const flag = localStorage.getItem('vuelto:debug:home');
    if (flag === '1') return true;
    if (flag === '0') return false;
  }
  try {
    if (typeof process !== 'undefined' && process.env?.EXPO_PUBLIC_DEBUG_HOME === '1') {
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

function nowMs(): string {
  if (typeof performance !== 'undefined') {
    return `${performance.now().toFixed(0)}`;
  }
  return '?';
}

export function homeDbg(scope: string, msg: string, extra?: Record<string, unknown>): void {
  if (!isHomeDebugEnabled()) return;
  if (extra != null) {
    console.log(`${PREFIX} +${nowMs()}ms ${scope} — ${msg}`, extra);
  } else {
    console.log(`${PREFIX} +${nowMs()}ms ${scope} — ${msg}`);
  }
}

const renderCounts = new Map<string, number>();

/** Cuenta renders; loguea los primeros 5 y cada 10. */
export function homeDbgRender(component: string): number {
  const n = (renderCounts.get(component) ?? 0) + 1;
  renderCounts.set(component, n);
  if (isHomeDebugEnabled() && (n <= 5 || n % 10 === 0)) {
    homeDbg(component, `render #${n}`);
  }
  return n;
}

export async function homeDbgTimed<T>(
  scope: string,
  step: string,
  fn: () => Promise<T>,
): Promise<T> {
  if (!isHomeDebugEnabled()) return fn();
  const t0 = performance.now();
  homeDbg(scope, `${step} → start`);
  try {
    const result = await fn();
    homeDbg(scope, `${step} → ok (${(performance.now() - t0).toFixed(0)}ms)`);
    return result;
  } catch (e) {
    homeDbg(scope, `${step} → FAIL (${(performance.now() - t0).toFixed(0)}ms)`, {
      error: e instanceof Error ? e.message : String(e),
    });
    throw e;
  }
}

/** Web: cada 4s loguea qué elemento captura el centro del viewport (overlay invisible). */
export function homeDbgStartPointerProbe(scope = 'pointer'): (() => void) | undefined {
  if (!isHomeDebugEnabled() || Platform.OS !== 'web' || typeof document === 'undefined') {
    return undefined;
  }
  homeDbg(scope, 'probe ON — filtrá consola por HomeDbg');
  const id = setInterval(() => {
    const x = Math.floor(window.innerWidth / 2);
    const y = Math.floor(window.innerHeight / 2);
    const el = document.elementFromPoint(x, y);
    const rect = el?.getBoundingClientRect?.();
    homeDbg(scope, `center (${x},${y})`, {
      tag: el?.tagName ?? null,
      testID: (el as HTMLElement)?.getAttribute?.('data-testid') ?? null,
      ariaHidden: Boolean(el?.closest?.('[aria-hidden="true"]')),
      w: rect?.width != null ? Math.round(rect.width) : null,
      h: rect?.height != null ? Math.round(rect.height) : null,
    });
  }, 4000);
  return () => clearInterval(id);
}
